import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useTransform } from "framer-motion";

const cinematicEase = (value) => 1 - Math.pow(1 - value, 3);

export const useInViewOnce = (margin = "-8% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof window.IntersectionObserver !== "function") {
      setInView(true);
      return undefined;
    }
    let observerReported = false;
    let fallbackTimer = 0;
    let initialFrame = 0;
    const revealIfVisible = () => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) setInView(true);
    };
    const io = new IntersectionObserver(
      (entries) => {
        observerReported = true;
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: margin, threshold: 0.01 }
    );
    io.observe(el);
    initialFrame = requestAnimationFrame(revealIfVisible);
    fallbackTimer = window.setTimeout(() => {
      if (!observerReported) setInView(true);
    }, 1200);
    window.addEventListener("pageshow", revealIfVisible);
    window.addEventListener("threshold:layout-ready", revealIfVisible);
    return () => {
      io.disconnect();
      cancelAnimationFrame(initialFrame);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("pageshow", revealIfVisible);
      window.removeEventListener("threshold:layout-ready", revealIfVisible);
    };
  }, [margin]);
  return [ref, inView];
};

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0 }) => {
  const [ref, inView] = useInViewOnce();
  return (
    <div ref={ref} className={`${className} ${inView ? "is-inview" : ""}`}>
      {lines.map((l, i) => (
        <span key={l} className="th-mask">
          <span className={`th-mask-inner ${lineClassName}`} style={{ transitionDelay: `${delay + i * 0.09}s` }}>
            {l}
          </span>
        </span>
      ))}
    </div>
  );
};

export const FadeUp = ({ children, className = "", delay = 0, ...rest }) => {
  const [ref, inView] = useInViewOnce();
  return (
    <div
      ref={ref}
      className={`th-fadeup ${className} ${inView ? "is-inview" : ""}`}
      style={{ transitionDelay: `${delay}s` }}
      {...rest}
    >
      {children}
    </div>
  );
};

export const Stage = ({ progress, range, hold = false, loadIn = false, className = "", children }) => {
  const oStops = useMemo(() => (hold || loadIn ? [range[0], range[1]] : range), [hold, loadIn, range]);
  const oVals = useMemo(() => (loadIn ? [1, 0] : hold ? [0, 1] : [0, 1, 1, 0]), [hold, loadIn]);
  const yStops = useMemo(() => [range[0], range[1]], [range]);
  const yValues = useMemo(() => (loadIn ? [0, -30] : [36, 0]), [loadIn]);
  const opacity = useTransform(progress, oStops, oVals, { ease: cinematicEase });
  const y = useTransform(progress, yStops, yValues, { ease: cinematicEase });
  return (
    <motion.div style={{ opacity, y }} className={`th-stage ${className}`}>
      {children}
    </motion.div>
  );
};

export const ChapterTag = ({ index, name }) => (
  <div className="th-chaptertag" data-testid={`chapter-tag-${index}`}>
    <span>{index}</span>
    <i />
    <em>{name}</em>
  </div>
);
